<template>
  <h1>Orders with Credit Applied</h1>

  <div class="content-section filters">

    <div class="alert alert-warning" role="alert">After setting filters or changing sorting order, click "Apply filters"
      to re-query the database</div>
    <div class="row">
      <div class="col-md-4">
        <FormKit
            type="datepicker"
            label="Start Date"
            v-model="selectedStartDate"
            format="DD/MM/YYYY"
            timezone="Europe/London"
            clearable
            :key="datepickerClearedKey"
        ></FormKit>
      </div>

      <div class="col-md-4">
        <FormKit
            type="datepicker"
            label="End Date"
            v-model="selectedEndDate"
            format="DD/MM/YYYY"
            timezone="Europe/London"
            clearable
            :key="datepickerClearedKey"
        ></FormKit>
      </div>
    </div>

    <div class="action-bar">
      <button @click="clearFilter" id="clearFilter">Clear filter</button>&#160;
      <button @click="exportZip" id="exportZip">Export Orders</button>&#160;
      <button @click="applyFilter" id="applyFilter1">Apply filter</button>
    </div>

    <div v-if="isListing" class="report-credits-applied">

      <easy-data-table
          :server-options="serverOptions"
          :server-items-length="serverItemsLength"
          @update:server-options="handleOptionsUpdate"
          :headers="headers"
          :items="ordersCredit"
          :search-field="searchField"
          :search-value="searchValue"
          :loading="loading"
          alternating
          border-cell
          buttons-pagination
          :prevent-context-menu-row="false"
      >
        <template #header="header">
          <div class="customize-header">

            {{ header.text }}<br />

             <input type="text" :id="'filter-' + header.value"
                   @input="debouncedApplyFilter"
                   @click.stop />
          </div>
        </template>


        <template #item-orderid="item">
          <router-link :to="{ name: 'ViewOrder', params: { id: item.order_id } }">
            <div>{{ item.order_id }}</div>
          </router-link>
        </template>

        <template #item-timeplaced="item">
          {{formatDateTimeEN(item.timeplaced)}}
        </template>

        <template #item-datedue="item">
          {{formatDateEN(item.datedue)}}
        </template>

        <template #item-customer_name="item">
          <router-link :to="{ name: 'ViewCustomer', params: { id: item.customer.customer_id } }">
            <div> {{item.customer.first_name}} {{item.customer.last_name}}</div>
          </router-link>
        </template>

        <template #item-customer_id="item">
          <router-link :to="{ name: 'ViewCustomer', params: { id: item.customer_id } }">
            <div>{{ item.customer_id }}</div>
          </router-link>
        </template>

        <template #item-ordercredit="item"> 
          £{{ Math.abs(item.ordercredit).toFixed(2) }}
        </template>

      </easy-data-table>
    </div>
  </div>
</template>


<script setup>
import {ref, watch} from "vue";
import OrderService from "@/services/OrderService";
import { has } from "lodash";
import { formatDateEN, formatDateYMD, formatDateTimeEN, convertDateToYMD } from '@/utils/dateUtils';
import { debounce } from 'lodash';

const ordersCredit =  ref([]);
const searchField = ref("datedue");
const searchValue = ref("");

const nowDate = new Date();
const currentDate = new Date(Date.UTC(nowDate.getFullYear(), nowDate.getMonth(), nowDate.getDate()));
const defaultStartDate = new Date(new Date().setDate(currentDate.getDate() - 90));
const defaultEndDate = new Date(new Date().setDate(currentDate.getDate() + 7));

const selectedStartDate = ref(defaultStartDate);
const selectedEndDate = ref(defaultEndDate);
const datepickerClearedKey = ref(0);

const forceRenderDatepicker = () => {
  datepickerClearedKey.value += 1;
};
const headers = [
  { text: "Order ID", value: "order_id", sortable: true  },
  { text: "Customer Name", value: "customer_name", sortable: true  },
  { text: "Customer ID", value: "customer_id", sortable: true  },
  { text: "Date Placed", value: "timeplaced", sortable: true  },
  { text: "Date Due", value: "datedue", sortable: true  },
  { text: "Credit Applied", value: "ordercredit", sortable: true },
];

const isListing = ref(true);

const loading = ref(false);
const serverItemsLength = ref(0);
const rowsPerPageSelect = ref(25);
const serverOptionsCustom = ref({
  page: 1,
  rowsPerPage: rowsPerPageSelect.value,
  sortBy: ["orderid"],
  sortType: ["desc"],
  exportFormat: "none",
  start_date: formatDateYMD(defaultStartDate),
  end_date: formatDateYMD(defaultEndDate),
  has_credit: 1,
  status_filter: "",
  search: "",
  rfid_account: -1,
  filters: {}
});
const serverOptions = ref(JSON.parse(JSON.stringify(serverOptionsCustom.value)));

const loadFromServer = async () => {
  loading.value = true;
  const {
    serverCurrentPageItems,
    serverTotalItemsLength,
  } = await OrderService.searchOrdersCredit(serverOptions.value);
  ordersCredit.value = serverCurrentPageItems;
  serverItemsLength.value = serverTotalItemsLength;
  loading.value = false;
};

const applyFilter = (resetPage) => {
  let filters = {};
  let updateOptions = serverOptions.value;
  
  for (let i = 0; i < headers.length; i++) {
    const originalFilterProperty = headers[i].value; // Use a new const for the original name
    if (originalFilterProperty !== "operations") {
      const filterInput = document.getElementById("filter-" + originalFilterProperty);
      if (filterInput && filterInput.value)  {
        // Use a 'let' variable for the key that will be sent to the backend
        let backendFilterKey = originalFilterProperty; 
        if (originalFilterProperty === "datedue") {
          backendFilterKey = "date_due";
          //convert date format from DD/MM/YYYY to YYYY-MM-DD
          filters[backendFilterKey] = convertDateToYMD(filterInput.value); 
        } 
        else if (originalFilterProperty === "timeplaced") {
          backendFilterKey = "time_placed";
          //convert date format from DD/MM/YYYY to YYYY-MM-DD
           filters[backendFilterKey] = convertDateToYMD(filterInput.value); 
        } else {
          // Assign the value to the correct backend key
          filters[backendFilterKey] = filterInput.value; 
        }
       
       
      }
    }
  }
  updateOptions.filters = filters;

  if (selectedStartDate.value) {
    updateOptions.start_date = formatDateYMD(new Date(selectedStartDate.value));
  } else {
    updateOptions.start_date = '';
  }

  if (selectedEndDate.value) {
    updateOptions.end_date = formatDateYMD(new Date(selectedEndDate.value));
  } else {
    updateOptions.end_date = '';
  }
  if (resetPage) {
    updateOptions.page = 1;
  }
  if (updateOptions.sortBy === "datedue") {
    updateOptions.sortBy = "date_due";
  }
  if (updateOptions.sortBy === "timeplaced") {
    updateOptions.sortBy = "time_placed";
  }
  if (updateOptions.sortBy === "orderid") {
    updateOptions.sortBy = "order_id";
  }
  serverOptions.value = updateOptions;
  loadFromServer();
};
const debouncedApplyFilter = debounce(applyFilter, 400);

const exportZip = async () => {
  loading.value = true;
  await OrderService.downloadOrdersCredit(serverOptions.value);
  loading.value = false;
};

const clearFilter = () => {
  //clear any existing filter inputs
  for (let i = 0; i < headers.length; i++) {
    const filterProperty = headers[i].value;
    if (filterProperty !== "operations") {
      document.getElementById("filter-" + filterProperty).value = "";
    }
  }
  // Reset the date picker models to their default values
  selectedStartDate.value = defaultStartDate;
  selectedEndDate.value = defaultEndDate;
  forceRenderDatepicker();
  
  // Reset the serverOptions object to its initial default state.
  // This will automatically update the table's page, sort order, and rows per page.
  // We use a deep copy to ensure reactivity works correctly.
  
  serverOptionsCustom.value = {
    page: 1,
    rowsPerPage: rowsPerPageSelect.value,
    sortBy: ["orderid"],
    sortType: ["desc"],
    exportFormat: "none",
    start_date: formatDateYMD(defaultStartDate),
    end_date: formatDateYMD(defaultEndDate),
    has_credit: 1,
    status_filter: "",
    search: "",
    rfid_account: -1,
    filters: {}
  }
  serverOptions.value = JSON.parse(JSON.stringify(serverOptionsCustom.value));
  
  // Reload the data from the server with the newly reset options.
  loadFromServer();
};

// initial load
loadFromServer();
const handleOptionsUpdate = (newOptions) => { 
  // Merge the new options from the table (page, rowsPerPage, sortBy, sortType)
  // into our existing serverOptions object to preserve custom parameters.
  serverOptions.value = { ...serverOptions.value, ...newOptions };
  
  applyFilter(false);
};
/*watch(() => [serverOptions.value.sortBy, serverOptions.value.sortType], () => {
 // serverOptions.value.sortBy = serverOptions.value.sortBy.length ? serverOptions.value.sortBy : ["orderid"];
 // serverOptions.value.sortType = serverOptions.value.sortType.length ? serverOptions.value.sortType : ["desc"];
  debouncedApplyFilter();
}, { deep: true });*/

</script>

<style scoped>

.report-credits-applied table,
.report-credits-applied tbody,
.report-credits-applied tr,
.report-credits-applied td {
  position: relative;
  z-index: 1;
}
</style>